import * as CL from '@design-system/component-library';
import { useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { useSubmitLead } from '../../common/hooks/useSubmitLead.js';
import { businessIdMsg, firstNameMsg, lastNameMsg, t } from '../../common/i18n/index.js';
import { Email, Name, PhoneNumber } from '../../common/react-hook-form/fields';
import { BusinessId } from '../../common/react-hook-form/fields/BusinessId.js';
import { Lead } from '../../generated/api/lead.js';
import { SiteContext } from '../../public/site/SiteContext.js';
import type { LeadContactDetails } from '../../generated/api/leadContactDetails.js';

type Question = {
  question: string;
  answer: string;
};

interface VaihtoetuLeadDetailsProps {
  modelName: string;
  answeredQuestions: Question[];
  offeredPrice: number;
  currency: string;
  onLeadSubmitted: () => void;
}

type VaihtoetuLeadData = {
  businessId: string;
  businessName: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
};

export const VaihtoetuLead = ({
  modelName,
  answeredQuestions,
  offeredPrice,
  currency,
  onLeadSubmitted,
}: VaihtoetuLeadDetailsProps) => {
  const methods = useForm({ mode: 'all' });
  const { siteBaseUrl } = useContext(SiteContext);
  const { pathname } = useLocation();
  const { leadState, submitLead } = useSubmitLead();

  const onSubmit = async (data: VaihtoetuLeadData) => {
    const contactDetails: LeadContactDetails = {
      businessId: data.businessId,
      businessName: '',
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phoneNumber: data.phoneNumber,
    };
    const lead: Lead = {
      leadType: Lead.LeadTypeEnum.ONLINE_CONTACT,
      description:
        `${modelName} - ${offeredPrice}${currency}\n` +
        answeredQuestions.map(q => `${q.question} - ${q.answer}`).join('\n'),
      sourceUrl: siteBaseUrl + pathname,
      formParameters: {
        businessId: data.businessId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
      },
    };
    const success = await submitLead({ lead, contactDetails });
    if (success) {
      onLeadSubmitted();
    }
  };

  return (
    <div>
      <h3 className={dsClass.H3_BOOK}>{t.OM46('Would you like to take advantage of the Elisa Vaihtoetu benefit?')}</h3>
      <p>{t.UGFH('Leave your contact details and our customer service team will contact you within 24 hours.')}</p>

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <CL.GridRow>
            <div className={`${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_L_4}`}>
              <Name name="firstName" label={t.AIK7(firstNameMsg)} placeholder={t.AIK7(firstNameMsg)} />
            </div>
            <div className={`${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_L_4}`}>
              <Name name="lastName" label={t.Y41S(lastNameMsg)} placeholder={t.Y41S(lastNameMsg)} />
            </div>
          </CL.GridRow>
          <CL.GridRow>
            <div className={`${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_L_4}`}>
              <Email name="email" />
            </div>
            <div className={`${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_L_4}`}>
              <PhoneNumber name="phoneNumber" />
            </div>
            <div className={`${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_L_4}`}>
              <BusinessId name="businessId" label={t.MPA5(businessIdMsg)} placeholder={t.MPA5(businessIdMsg)} />
            </div>
          </CL.GridRow>
          <div className={`${dsClass.GRID_ROW} ${dsClass.MARGIN_BOTTOM_3}`}>
            <CL.Link
              linkStyle="link"
              linkTarget="_blank"
              linkHref="https://yrityksille.elisa.fi/ohje/elisan-sopimusehdot-yritysasiakkaille"
            >
              {t.ND8W('Terms and conditions')}
            </CL.Link>
          </div>
          {leadState === 'SUCCESS' && !offeredPrice && (
            <p className={dsClass.TEXT_BOLD}>
              {t.VLT2(
                'Thank you, our sales representative will be in touch with you shortly! You can now fill in the details for another device.'
              )}
            </p>
          )}
          <div className={dsClass.GRID_ROW}>
            <CL.Button
              className="of-vaihtoetu__form-button"
              color="primary"
              size="l"
              type="submit"
              loading={leadState === 'IN_PROGRESS'}
              disabled={!offeredPrice}
            >
              {t.GWH6('Send request')}
            </CL.Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
